import React, { Suspense, useEffect, useState } from 'react';
import './App.scss';
import { Canvas } from '@react-three/fiber';
import { Loader, useProgress } from '@react-three/drei';
import {
  Routes,
  Route,
  BrowserRouter as Router,
  useLocation,
} from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

// components and routes
import GlobalScene from './components/GlobalScene/GlobalScene';
import Navbar from './components/Navbar/Navbar';
import Home from './routes/Home/Home';
import Hello from './routes/Hello/Hello';
import Work from './routes/Work/Work';
import Connect from './routes/Connect/Connect';
import LLMoid from './routes/LLMoid/LLMoid';

import AnimatedCursor from './components/AnimatedCursor/AnimatedCursor';

function App() {
  const { progress } = useProgress();
  const [resizeKey, setResizeKey] = useState(0);
  const [cursorSizes, setCursorSizes] = useState({ inner: 8, outer: 28 });

  // Calculate cursor sizes in rem
  // innerSize: 0.5rem, outerSize: 1.75rem
  const innerSizeRem = 0.5;
  const outerSizeRem = 1.75;

  const calculateRemInPixels = remValue => {
    if (typeof window !== 'undefined' && document.documentElement) {
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      return remValue * rootFontSize;
    }
    // Fallback: assume 1rem = 16px if calculation not available
    return remValue * 16;
  };

  useEffect(() => {
    const updateCursorSizes = () => {
      setCursorSizes({
        inner: calculateRemInPixels(innerSizeRem),
        outer: calculateRemInPixels(outerSizeRem),
      });
    };

    // Calculate initial sizes
    updateCursorSizes();

    let resizeTimer;
    const handleResize = () => {
      // Clear the previous timer
      clearTimeout(resizeTimer);
      // Set a new timer to detect when resize ends
      resizeTimer = setTimeout(() => {
        // Update cursor sizes on resize
        updateCursorSizes();
        // Force re-render by updating the key
        setResizeKey(prev => prev + 1);
      }, 150); // Wait 150ms after resize ends
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  return (
    <div className="App">
      <Router>
        <AnimatedCursor
          innerSize={cursorSizes.inner}
          outerSize={cursorSizes.outer}
          color="193, 11, 111"
          outerAlpha={0.2}
          innerScale={0.7}
          outerScale={3}
          clickables={[
            'a',
            'input[type="text"]',
            'input[type="email"]',
            'input[type="number"]',
            'input[type="submit"]',
            'input[type="image"]',
            'label[for]',
            'select',
            'textarea',
            'button',
            '.link',
          ]}
        />
        <div
          key={resizeKey}
          style={progress !== 100 ? { opacity: 0 } : { opacity: 1 }}
          className="scene-container"
        >
          <Suspense fallback={null}>
            <Canvas
              dpr={[1, 1]}
              camera={{
                fov: 1,
              }}
            >
              <GlobalScene />
            </Canvas>
          </Suspense>
          {progress === 100 && (
            <>
              <Navbar />
              <RouteTransition />
            </>
          )}
        </div>
        <Loader />
      </Router>
    </div>
  );
}

function RouteTransition() {
  const location = useLocation();

  return (
    <TransitionGroup component={null}>
      <CSSTransition
        key={location.pathname}
        timeout={500}
        classNames="page"
        unmountOnExit
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/hello" element={<Hello />} />
          <Route path="/work" element={<Work />} />
          <Route path="/connect" element={<Connect />} />
          <Route path="/llmoid" element={<LLMoid />} />
        </Routes>
      </CSSTransition>
    </TransitionGroup>
  );
}

export default App;
